import { useEffect, useState } from 'react';

const DAY = 86400000;
const HOUR = 3600000;
const MINUTE = 60000;

function partsUntil(time) {
  const diff = Math.max(time - Date.now(), 0);
  return {
    days: Math.floor(diff / DAY),
    hours: Math.floor((diff % DAY) / HOUR),
    minutes: Math.floor((diff % HOUR) / MINUTE),
    seconds: Math.floor((diff % MINUTE) / 1000),
  };
}

/**
 * Ticks every second toward `endDate` — the countdown shared by the Overview
 * hero, the Competition Detail hero and every card in the list.
 *
 * Keyed on the target's time VALUE, not on the Date object. The list cards
 * build `new Date(...)` inline on every render, so an identity-keyed effect
 * re-ran on every render, called `setParts` with a fresh object, and
 * re-rendered — a loop that hit React's update-depth limit and froze the whole
 * tree. A frozen tree still paints what it last committed, so the symptom was
 * a click that changed the URL while the page stayed put until a reload.
 *
 * An unreadable date counts down from zero rather than rendering NaN.
 */
export default function useCountdown(endDate) {
  const ms = endDate instanceof Date ? endDate.getTime() : new Date(endDate).getTime();
  const time = Number.isFinite(ms) ? ms : 0;

  const [parts, setParts] = useState(() => partsUntil(time));

  useEffect(() => {
    setParts(partsUntil(time));
    const id = setInterval(() => setParts(partsUntil(time)), 1000);
    return () => clearInterval(id);
  }, [time]);

  return parts;
}
